import { useEffect } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import * as chatSocket from '../../lib/chatSocket';
import * as commentsApi from '../../api/comments';
import {
  COMMENT_EVENTS,
  type Comment,
  type CommentPage,
  type CommentTargetType,
  type CommentsChangedPayload,
} from '../../types/comments';
import { commentKeys } from './commentKeys';

/**
 * Subscribes to `comments:{target_type}:{target_id}` for the duration of the
 * component's lifetime and dispatches `comments:changed` events into the
 * TanStack cache (focused refetches). Reuses the existing chat socket
 * singleton — no second connection.
 */
export function useCommentSocket(
  targetType: CommentTargetType,
  targetId: string | null | undefined,
  enabled: boolean
): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled || !targetId) return;
    const socket = chatSocket.getSocket();

    const subscribe = () => {
      chatSocket
        .getSocket()
        ?.emit(COMMENT_EVENTS.SUBSCRIBE, { target_type: targetType, target_id: targetId });
    };

    if (socket?.connected) {
      subscribe();
    }
    // Re-subscribe on every (re)connect since the server forgets rooms on
    // disconnect.
    const offConnect = chatSocket.on('connect', subscribe);

    const offChanged = chatSocket.on(COMMENT_EVENTS.CHANGED, (...args) => {
      const payload = args[0] as CommentsChangedPayload;
      if (!payload) return;
      if (payload.target_type !== targetType || payload.target_id !== targetId) return;

      switch (payload.action) {
        case 'created': {
          if (payload.parent_id) {
            // Reply: invalidate the parent's replies cache (if loaded) and
            // bump the parent's reply_count.
            qc.invalidateQueries({ queryKey: commentKeys.replies(payload.parent_id) });
            qc.setQueryData<InfiniteData<CommentPage>>(
              commentKeys.list(targetType, targetId),
              (prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  pages: prev.pages.map((p) => ({
                    ...p,
                    comments: p.comments.map((c) =>
                      c.id === payload.parent_id
                        ? { ...c, reply_count: c.reply_count + 1 }
                        : c
                    ),
                  })),
                };
              }
            );
          } else {
            qc.invalidateQueries({ queryKey: commentKeys.list(targetType, targetId) });
          }
          break;
        }
        case 'updated':
        case 'reacted':
        case 'deleted': {
          // Focused refetch of the single comment, then patch in place
          // wherever it lives (top-level or under a parent).
          commentsApi
            .getComment(payload.comment_id)
            .then((fresh: Comment) => {
              const replace = (c: Comment): Comment => (c.id === fresh.id ? fresh : c);
              qc.setQueryData<InfiniteData<CommentPage>>(
                commentKeys.list(targetType, targetId),
                (prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    pages: prev.pages.map((p) => ({ ...p, comments: p.comments.map(replace) })),
                  };
                }
              );
              if (fresh.parent_id) {
                qc.setQueryData<InfiniteData<CommentPage>>(
                  commentKeys.replies(fresh.parent_id),
                  (prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      pages: prev.pages.map((p) => ({
                        ...p,
                        comments: p.comments.map(replace),
                      })),
                    };
                  }
                );
              }
            })
            .catch(() => {
              // 404 (deleted hard) or 403 — fall back to a list invalidate.
              qc.invalidateQueries({ queryKey: commentKeys.list(targetType, targetId) });
            });
          break;
        }
      }
    });

    return () => {
      offConnect();
      offChanged();
      chatSocket
        .getSocket()
        ?.emit(COMMENT_EVENTS.UNSUBSCRIBE, { target_type: targetType, target_id: targetId });
    };
  }, [targetType, targetId, enabled, qc]);
}
